import { Component, createRef, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { capturePosthogEvent, capturePosthogException } from "config/posthog-runtime";
import { ANALYTICS_EVENTS } from "analytics";
import { ErrorDisplay } from "./components";
import { useErrorHandlerConnectivity } from "hooks/useErrorHandlerConnectivity";
import { shouldPassthroughAuthDuringMaintenance } from "utils/isAuthRoute";
import { isChunkLoadError } from "utils/isChunkLoadError";
import type { ErrorDisplayMode } from "./components/ErrorDisplay/ErrorDisplay.types";
import type { ErrorHandlerRef } from "./ErrorHandler.types";

export interface ErrorHandlerProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

export interface ErrorHandlerContentProps extends ErrorHandlerProps {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  reactRetryCount: number;
  onResetBoundary: () => void;
  onReload: () => void;
}

const ERROR_LAYOUT_CLASS = "min-h-screen bg-card flex items-center justify-center p-4";

const resolveConnectivityMode = (args: {
  isOnline: boolean;
  isMaintenance: boolean;
}): ErrorDisplayMode => {
  if (!args.isOnline) return "offline";
  if (args.isMaintenance) return "maintenance";
  return "service_unavailable";
};

const ErrorHandlerContent = forwardRef<ErrorHandlerRef, ErrorHandlerContentProps>(
  function ErrorHandlerContent(
    { children, fallback, hasError, error, errorInfo, reactRetryCount, onResetBoundary, onReload },
    ref
  ) {
    const connectivity = useErrorHandlerConnectivity();
    const { pathname } = useLocation();
    const shownReactErrorRef = useRef(false);

    useImperativeHandle(
      ref,
      () => ({
        handleBackendError: connectivity.handleBackendError,
        detectLoadingStuck: connectivity.detectLoadingStuck,
        getBackendStatus: connectivity.getBackendStatus,
      }),
      [
        connectivity.handleBackendError,
        connectivity.detectLoadingStuck,
        connectivity.getBackendStatus,
      ]
    );

    const passthroughAuthDuringMaintenance = shouldPassthroughAuthDuringMaintenance({
      pathname,
      isMaintenance: connectivity.isMaintenance,
      isOnline: connectivity.isOnline,
      isBackendAvailable: connectivity.isBackendAvailable,
    });

    const showConnectivityError =
      !connectivity.isOnline ||
      (!connectivity.isBackendAvailable && !passthroughAuthDuringMaintenance);

    const isStaleClient = Boolean(error && isChunkLoadError(error));

    useEffect(() => {
      if (!hasError || showConnectivityError || shownReactErrorRef.current) return;
      shownReactErrorRef.current = true;
      capturePosthogEvent(ANALYTICS_EVENTS.APP_ERROR_SHOWN, {
        reason: isStaleClient ? "stale_client" : "react_error",
      });
    }, [hasError, showConnectivityError, isStaleClient]);

    useEffect(() => {
      if (!hasError) {
        shownReactErrorRef.current = false;
      }
    }, [hasError]);

    if (showConnectivityError) {
      const mode = resolveConnectivityMode({
        isOnline: connectivity.isOnline,
        isMaintenance: connectivity.isMaintenance,
      });
      return (
        <div className={ERROR_LAYOUT_CLASS}>
          <div className="max-w-md w-full">
            <ErrorDisplay
              mode={mode}
              isChecking={connectivity.isFetching}
              onPrimaryAction={connectivity.handleCheckNow}
              error={connectivity.lastError}
              maintenanceDetails={connectivity.maintenanceDetails}
            />
          </div>
        </div>
      );
    }

    if (hasError) {
      if (fallback) return <>{fallback}</>;

      if (isStaleClient) {
        return (
          <div className={ERROR_LAYOUT_CLASS}>
            <div className="max-w-md w-full">
              <ErrorDisplay
                mode="stale_client"
                error={error}
                errorInfo={errorInfo}
                onPrimaryAction={() => {
                  capturePosthogEvent(ANALYTICS_EVENTS.APP_ERROR_RETRY_CLICKED, {
                    reason: "stale_client",
                    action: "reload",
                  });
                  onReload();
                }}
              />
            </div>
          </div>
        );
      }

      const showReloadEscalation = reactRetryCount >= 1;

      return (
        <div className={ERROR_LAYOUT_CLASS}>
          <div className="max-w-md w-full">
            <ErrorDisplay
              mode="react_error"
              error={error}
              errorInfo={errorInfo}
              onPrimaryAction={() => {
                capturePosthogEvent(ANALYTICS_EVENTS.APP_ERROR_RETRY_CLICKED, {
                  reason: "react_error",
                  action: "reset_boundary",
                });
                onResetBoundary();
              }}
              onSecondaryAction={
                showReloadEscalation
                  ? () => {
                      capturePosthogEvent(ANALYTICS_EVENTS.APP_ERROR_RETRY_CLICKED, {
                        reason: "react_error",
                        action: "reload",
                      });
                      onReload();
                    }
                  : undefined
              }
              secondaryLabel={showReloadEscalation ? "Reload RivetAI" : undefined}
            />
          </div>
        </div>
      );
    }

    return <>{children}</>;
  }
);

interface ErrorHandlerState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  reactRetryCount: number;
}

export class ErrorHandler extends Component<ErrorHandlerProps, ErrorHandlerState> {
  static getDerivedStateFromError(error: Error): Partial<ErrorHandlerState> {
    return { hasError: true, error };
  }

  private contentRef = createRef<ErrorHandlerRef>();

  state: ErrorHandlerState = {
    hasError: false,
    reactRetryCount: 0,
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
    capturePosthogException(error, { component_stack: errorInfo.componentStack });
    this.props.onError?.(error, errorInfo);
  }

  resetErrorBoundary = () => {
    this.setState((prev) => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      reactRetryCount: prev.reactRetryCount + 1,
    }));
  };

  reloadApp = () => {
    window.location.reload();
  };

  handleBackendError = () => {
    this.contentRef.current?.handleBackendError();
  };

  detectLoadingStuck = () => this.contentRef.current?.detectLoadingStuck() ?? false;

  getBackendStatus = () =>
    this.contentRef.current?.getBackendStatus() ?? {
      isOnline: typeof navigator !== "undefined" && navigator.onLine,
      isBackendAvailable: true,
      lastError: undefined,
      retryCount: 0,
    };

  render() {
    return (
      <ErrorHandlerContent
        ref={this.contentRef}
        {...this.props}
        hasError={this.state.hasError}
        error={this.state.error}
        errorInfo={this.state.errorInfo}
        reactRetryCount={this.state.reactRetryCount}
        onResetBoundary={this.resetErrorBoundary}
        onReload={this.reloadApp}
      />
    );
  }
}
